import axios, { AxiosHeaders } from 'axios';
import { isEmpty } from 'lodash';

import { Family, SegmentType } from '@gn-audio/jss-core';

import { PIMProduct } from 'components/common/MagicCards/MagicCards';
import getConfig from 'temp/config';

const config = getConfig();
const productSearchFilter =
  "not (pageUrl eq null and supportPageUrl eq null) and attributes/any(a: a eq 'Product_Portfolio|Jabra') and (attributes/all(a: a ne 'Meta_Refurbished|True') or attributes/any(a: a eq 'Meta_Accessorytype|17'))";
const emptyResult: EmptyResults = { count: 0, items: [] };

const failedToFetchSearchResultsLabel = 'Failed to fetch search results';

export const getFamilyGroups = async (
  familyId: string,
  locale: string
): Promise<FamilyGroup[] | string> => {
  const url = `${config.productCatalogueBaseUrl}/family/${familyId}/attributes?include=available&marketLocale=${locale}`;
  const response = await getWithApiKey(url);
  return response.data;
};

export const getGroupDetails = async (
  groupId: string,
  locale: string,
  productCatalogueApi?: string
): Promise<PIMProduct> => {
  const apiEndpoint = productCatalogueApi || config.productCatalogueBaseUrl;
  const url = `${apiEndpoint}/group/${groupId}?include=available&marketLocale=${locale}`;
  const response = await getWithApiKey(url);
  return response.data || {};
};

const getHeader = (): AxiosHeaders => {
  const headers = new AxiosHeaders();
  if (!isEmpty(config.productCatalogueApiKey)) {
    headers.set('api_key', config.productCatalogueApiKey);
  }
  return headers;
};
const getWithApiKey = async (url: string) => {
  const headers = getHeader();
  return await axios.get(url, { headers });
};

export const getFamilyItem = async (familyId: string, locale: string): Promise<FamilyGroup> => {
  const url = `${config.productCatalogueBaseUrl}/family/${familyId}?include=available&marketLocale=${locale}`;
  const response = await getWithApiKey(url);
  return response.data;
};

export const getFamilies = async (
  familyIds: (string | number)[],
  locale: string
): Promise<Family[]> => {
  if (isEmpty(familyIds)) return [];
  const url = `${config.productCatalogueBaseUrl}/family/filter?ids=${familyIds.join(
    '|'
  )}&include=available&marketLocale=${locale}`;

  const response = await getWithApiKey(url);
  return isEmpty(response.data) ? [] : response.data;
};

export const getAttributesByFamily = async (
  familyId: string,
  locale: string
): Promise<FamilyGroup[]> => {
  const url = `${config.productCatalogueBaseUrl}/Family/${familyId}/attributes?marketLocale=${locale}&include=available`;
  const response = await getWithApiKey(url);
  return isEmpty(response.data) ? [] : response.data;
};

export const getAttributesByGroups = async (
  productIds: string[],
  locale: string,
  productCatalogueApi?: string
): Promise<FamilyGroup[]> => {
  const apiEndpoint = productCatalogueApi || config.productCatalogueBaseUrl;
  const url = `${apiEndpoint}/group/attributes?ids=${productIds.join(
    '|'
  )}&include=available&marketLocale=${locale}`;

  const response = await getWithApiKey(url);
  return isEmpty(response.data) ? [] : response.data;
};

export const getFamilyKSP = async (familyId: string, locale: string): Promise<KSP[]> => {
  const url = `${config.productCatalogueBaseUrl}/family/${familyId}/keysellingpoints?include=available&marketLocale=${locale}&kspMode=short`;
  const res = await getWithApiKey(url);
  if (res.status !== 200) {
    return [];
  }
  return res.data;
};

export const getGroupKSP = async (groupId: string, locale: string): Promise<KSP[]> => {
  const url = `${config.productCatalogueBaseUrl}/group/${groupId}/keysellingpoints?include=available&marketLocale=${locale}&kspMode=short`;
  const res = await getWithApiKey(url);
  if (res.status !== 200) {
    return [];
  }
  return res.data;
};

export const getProductsByANCType = async (
  ancType: '1' | '2' | '3' | '4'
): Promise<SearchResults | EmptyResults> => {
  const headers = getHeader();
  headers.set('Content-Type', 'application/json');
  try {
    const url = `${config.productCatalogueBaseUrl}/Search`;
    const response = await axios({
      url,
      method: 'POST',
      headers: headers,
      data: {
        marketLocale: 'en-us',
        search: '',
        filter: `attributes/any(a: a eq 'Product_AncTypePublicWeb|${ancType}')`,
        orderBy: 'discountedPrice desc',
        skip: 0,
        top: 10,
      },
    });

    if (response.status === 204) {
      return { ...emptyResult };
    } else {
      return response.data;
    }
  } catch (error) {
    console.error(`${failedToFetchSearchResultsLabel}:`, error);
    return { ...emptyResult, error: error };
  }
};

export const SegmentTypes: Record<string, SegmentType> = {
  Business: 'Business' as SegmentType,
  Convergence: 'Convergence' as SegmentType,
  Consumer: 'Consumer' as SegmentType,
};

export const getSearchResults = async (
  productCatalogueBaseUrl: string,
  searchQuery: string,
  skip = 0,
  numberOfResultsPerPage: number,
  locale: string
): Promise<SearchResults | EmptyResults> => {
  const headers = getHeader();
  headers.set('Content-Type', 'application/json');
  try {
    const response = await axios({
      url: `${productCatalogueBaseUrl}/search/prioritize?include=available`,
      method: 'POST',
      headers,
      data: {
        filter: productSearchFilter,
        orderBy: '',
        search: searchQuery,
        skip: skip,
        top: numberOfResultsPerPage,
        marketLocale: locale === 'en' ? 'en-us' : locale,
      },
    });

    if (response.status === 204) {
      return { ...emptyResult };
    } else {
      return response.data;
    }
  } catch (error) {
    console.error(`${failedToFetchSearchResultsLabel}:`, error);
    return { ...emptyResult, error: error };
  }
};

export interface FamilyGroup {
  attributes: Attribute[];
  images: SCImage[];
  name: string;
  pageUrl: string;
  supportPageUrl: string;
  sku: string;
  model?: string;
  warranty?: string;
  productId?: number;
  groupId: number;
  groupState: number;
  hasBluetoothPairingGuide: boolean;
  familyId: number;
  familyName: string;
  productName: string;
  segmentType: SegmentType;
}

export interface Attribute {
  key: string;
  text?: string;
  value?: string;
  valueInEnUs?: string;
  valueKey: string;
}

export interface SCImage {
  url: string;
}

export interface KSP {
  title: string;
  header: string;
  text: string;
}

export type EmptyResults = {
  count: number;
  items: [];
  error?: unknown;
};

export type SearchResults = {
  count: number;
  items: SearchResultsItem[];
  error?: unknown;
};

export type SearchResultsItem = {
  attributes: string[];
  title: string;
  familyId: number;
  familyName: string;
  images: string[];
  pageUrl: string;
  supportPageUrl: string;
  uniqueSellingPoint: string;
};
